/**
 * Shopping Cart Context for Exam Platform
 * Provides cart state management with localStorage persistence for guests
 * Integrates with existing auth system and follows established patterns
 * 
 * Last update: Tue Jul  1 15:20:00 UTC 2025
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuthContext } from './AuthProvider';

const ShoppingCartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.examId === action.payload.examId);
      if (existingItem) {
        // For exams, don't increase quantity - just return existing state
        return state;
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      };

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.examId !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      // For exams, quantity is always 1 - no need to update
      return state;

    case 'CLEAR_CART':
      return { ...state, items: [] };

    case 'RESTORE_CART':
      return { ...state, items: action.payload };

    default:
      return state;
  }
};

export const ShoppingCartProvider = ({ children }) => {
  const { user } = useAuthContext();
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  // Persist cart to localStorage for all users
  useEffect(() => {
    const savedCart = localStorage.getItem('shoppingCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (parsedCart.items && Array.isArray(parsedCart.items)) {
          dispatch({ type: 'RESTORE_CART', payload: parsedCart.items });
        }
      } catch (error) {
        console.error('Error restoring cart from localStorage:', error);
      }
    }
  }, [user]);

  // Save cart to localStorage for all users
  useEffect(() => {
    if (state.items.length > 0) {
      localStorage.setItem('shoppingCart', JSON.stringify(state));
    }
  }, [state, user]);

  const addToCart = (exam) => {
    dispatch({ type: 'ADD_ITEM', payload: exam });
  };

  const removeFromCart = (examId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: examId });
  };

  const updateQuantity = (examId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { examId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem('shoppingCart');
  };

  const getTotal = () => {
    return state.items.reduce((total, item) => {
      const price = Number(item.price) || 0;
      const discount = Number(item.discount) || 0;
      const discountedPrice = price - (price * discount / 100);
      return total + (discountedPrice * item.quantity);
    }, 0);
  };

  const getItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <ShoppingCartContext.Provider value={{
      items: state.items,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getTotal,
      getItemCount
    }}>
      {children}
    </ShoppingCartContext.Provider>
  );
};

export const useShoppingCart = () => {
  const context = useContext(ShoppingCartContext);
  if (!context) {
    throw new Error('useShoppingCart must be used within a ShoppingCartProvider');
  }
  return context;
}; 